import {
  setEnrichment,
  setEnrichmentQualityScore,
} from "../../store/score/scoreSlice";
import React, { useState, useEffect, useRef, act } from "react";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { useDebugMode } from "../../shared/hooks";
import { JOB_GALLERY_STORAGE_KEY } from "./imageGallery";
import {
  ArrowUpRight,
  CheckCircle2,
  Clock,
  ShoppingCart,
  ChevronDown,
  ChevronUp,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { formatDate } from "../../admin/dashboards/enrichmentViewer/utils";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { useApi } from "../../hooks/useApi";
import { ProductQualityMetric } from "../../types/api";
import ProductDetail from "./ProductDetailView";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "../../components/ui/pagination";
import { Drawer, DrawerContent, DrawerFooter  } from "../../components/ui/drawer";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/hooks/use-toast";
import ExportControls from "./ExportControls";
import { setJobFilter } from "../../store/filtersSlice";
import { startJob } from "../../store/jobsSlice";
import { ExportJobService } from "../../services/job/ExportJobService";
import { updateEnrichmentTask2, startExportToShopify } from "wasp/client/operations";
import { EnrichmentJobWithQuality } from "../../types/api";
import { setHoveredJob } from "../../store/debugSlice";
import { useJobProgress } from "../../hooks/useJobProgress";
import { createJobStatusUrl } from "../../admin/dashboards/urls";
import { useQuery } from "wasp/client/operations";
import { getEnrichmentTask } from "wasp/client/operations";
import { useTranslation } from "../../i18n/hooks";

interface JobCompletedCardProps {
  job: EnrichmentJobWithQuality;
  isAnyJobExporting: boolean;
  isExpanded: boolean;
  expansionRect: DOMRect | null;
  onExpand: (rect: DOMRect) => void;
  onCollapse: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
}

const JobCompletedCard = ({
  job,
  isAnyJobExporting,
  isExpanded,
  expansionRect,
  onExpand,
  onCollapse,
  containerRef,
}: JobCompletedCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [expandedProductIds, setExpandedProductIds] = useState(
    new Set<string>()
  );
  const [isContentVisible, setIsContentVisible] = useState(false);
  const dispatch = useDispatch();
  const isDebugMode = useDebugMode();
  const { toast } = useToast();
  const isMobile = window.innerWidth < 768;
  const { t } = useTranslation();

  const activeJobs = Object.values(
    useSelector((state: RootState) => state.jobs.activeExportJobs)
  );

  // İhraç işlemi durumunu polling ile izleme
  useEffect(() => {
    // Bir ihraç işlemi aktif olduğunda (isExportingToShopify true olduğunda) polling başlat
    if (job.isExportingToShopify && job.enrichmentJobId) {
      
      // ExportJobService'i başlatarak polling'i aktifleştiriyoruz
      const exportService = ExportJobService.getInstance(dispatch);
      exportService.ensurePolling();
      
      // Component unmount olunca polling'i durduruyoruz
      return () => {
        // Eğer bu component unmount olurken başka export işlemleri yoksa polling'i durdur
      
        if (activeJobs.length <= 1) {
          exportService.stopPolling();
        }
      };
    }
  }, [job.isExportingToShopify, job.id, job.enrichmentJobId, dispatch, activeJobs]);

  const handleCardClick = () => {
    if (!cardRef.current || isExpanded) return;

    const rect = cardRef.current.getBoundingClientRect();
    onExpand(rect);
    if (overlayRef.current) {
      overlayRef.current.style.transform = `
        translate(${rect.left}px, ${rect.top}px)
        scale(${rect.width / window.innerWidth}, ${
          rect.height / window.innerHeight
        })
      `;
      overlayRef.current.style.opacity = "0";

      // Force reflow
      overlayRef.current.offsetHeight;

      // Animate to full screen
      overlayRef.current.style.transform = "none";
      overlayRef.current.style.opacity = "1";
    }
  };

  useEffect(() => {
    if (isExpanded) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") handleClose();
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded && overlayRef.current && expansionRect) {
      const overlay = overlayRef.current;

      // Set initial position
      overlay.style.transform = `
        translate(${expansionRect.left}px, ${expansionRect.top}px)
        scale(${expansionRect.width / window.innerWidth}, ${
          expansionRect.height / window.innerHeight
        })
      `;
      overlay.style.opacity = "0";

      // Force reflow
      overlay.offsetHeight;

      // Animate to full screen
      overlay.style.transition = "all 100ms cubic-bezier(0.4, 0, 0.2, 1)";
      overlay.style.transform = "none";
      overlay.style.opacity = "1";

      setIsContentVisible(true);
      // Just mark animation as complete after transition
    }
  }, [isExpanded, expansionRect]);

  const handleClose = () => {
    if (!cardRef.current || !isExpanded) return;

    setIsContentVisible(false);
    onCollapse();
  };

  const { currentUser, currentCollection } = useSelector(
    (state: RootState) => state.user
  );

  const jobFilter = useSelector((state: RootState) => state.filters.jobFilter);

  const { enrichmentPendingChanges } = useSelector(
    (state: RootState) => state.flow
  );

  const { enrichmentQualityScores } = useSelector(
    (state: RootState) => state.score
  );

  let enrichmentScore = job.enrichmentJobId
    ? enrichmentQualityScores[job.enrichmentJobId]?.calculated
    : {
        overallBefore: 0,
        overallAfter: 0,
      };

  const hasAnyChangesForJob = enrichmentPendingChanges.some(
    (change) => change.enrichmentJobId === job.enrichmentJobId
  );

  const hasAnyChanges = (productId: string) => {
    return enrichmentPendingChanges.some(
      (change) =>
        change.productId === productId &&
        change.enrichmentJobId === job.enrichmentJobId
    );
  };

  const [isDrawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (Array.from(expandedProductIds).length > 0 && isMobile) {
      setDrawerOpen(true);
    }
  }, [expandedProductIds]);

  useEffect(() => {
    if (isMobile && !isDrawerOpen) {
      setExpandedProductIds(new Set());
    }
  }, [isDrawerOpen]);

  const api = useApi();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Use placeholder data if API data is not available
  const { data: productsData, isLoading: isFetching } = api.useProducts({
    accountId: currentUser?.email ?? "",
    collectionName: currentCollection?.collection?.name ?? "",
    limit: ITEMS_PER_PAGE,
    offset: (currentPage - 1) * ITEMS_PER_PAGE, // Add offset for pagination
    sortField: "created_at",
    tableId: currentCollection?.collection?.tableId ?? "",
    filters: {
      quality_group: "all_products",
      tags: [],
      search: "",
      date_range: undefined,
      category_hierarchy: [],
      category_name: [],
      product_type: [],
    },
    metaFields: [],
    enrichmentJobId: job.enrichmentJobId ?? undefined,
  }) ?? { data: { products: [] }, isLoading: false };

  const { data: qualityData, isLoading: isQualityLoading } =
    api.useQualityScore(
      currentCollection?.collection?.tableId ?? "",
      job.enrichmentJobId ?? undefined
    );

  const getFirstChangedField = (product: ProductQualityMetric) => {
    const fields = [
      {
        name: "Title",
        old: product.old_title,
        new: product.title,
      },
      {
        name: "Description",
        old: product.old_description_html,
        new: product.description_html,
      },
      {
        name: "Description Bullets",
        old: product.old_description_bullets,
        new: product.description_bullets,
      },
      {
        name: "Category",
        old: product.old_category_name,
        new: product.category_name,
      },
      {
        name: "Category Full Name",
        old: product.old_category_full_name,
        new: product.category_full_name,
      },
      {
        name: "Product Type",
        old: product.old_product_type,
        new: product.product_type,
      },
      {
        name: "Tags",
        old: product.old_tags,
        new: product.tags,
      },
      {
        name: "SEO Description",
        old: product.old_seo_description,
        new: product.seo_description,
      },
    ];

    return fields.find((field) => field.old !== field.new);
  };

  useEffect(() => {
    if (productsData?.data?.products && job.enrichmentJobId) {
      dispatch(
        setEnrichment({
          enrichmentJobId: job.enrichmentJobId,
          products: productsData?.data?.products,
        })
      );
    }
  }, [productsData]);

  useEffect(() => {
    if (qualityData?.data && job.enrichmentJobId) {
      dispatch(
        setEnrichmentQualityScore({
          enrichmentJobId: job.enrichmentJobId,
          qualityScore: qualityData.data,
        })
      );
    }
  }, [qualityData]);

  const products = (productsData?.data?.products ?? []).map((product) => ({
    ...product,
    improvedTitle: product.title || "N/A  -- Need improved title",
    improvedDescription:
      product.description_html || "N/A -- Need improved description",
    oldQualityScore: product.old_quality_score || -1,
    newQualityScore: product.new_quality_score || -1,
    improvements: product.improvements || [
      "SEO Optimized",
      "Enhanced Description",
    ],
    // Keep any additional fields from the API
  })) as ProductQualityMetric[];

  // useEffect(() => {
  //   if (
  //     job.exportState === "initial"
  //   ) {
  //     for(var prod of products){
  //       if(prod.radar_images && prod.radar_images.length > 0){
  //         const cached = localStorage.getItem(`${JOB_GALLERY_STORAGE_KEY(job.enrichmentJobId!, prod.product_id)}`);
  //         dispatch(
  //           addPendingChange({
  //             enrichmentJobId: job.enrichmentJobId!,
  //             productId: prod.product_id,
  //             fieldName: "radar_images",
  //             fieldValue: cached ?? JSON.stringify([]),
  //           })
  //         );
  //       }
  //     }
  //   }
  // }, [products, job.exportState]);

  useEffect(() => {
    if (
      !isMobile &&
      isExpanded &&
      products.length > 0 &&
      expandedProductIds.size === 0
    ) {
      const firstProduct = products[0];
      setExpandedProductIds(new Set([`${firstProduct.product_id}_0`]));
    }
  }, [isExpanded, products, expandedProductIds]);

  const productCount =
    productsData?.data?.content_quality_overview.filtered_product_count;

  const exportToShopify = async ({
    exportedFields,
  }: {
    exportedFields?: string[];
  }) => {
    if (
      currentCollection?.collection?.tableId &&
      currentCollection?.collection?.miqShopifyDomain &&
      currentUser?.email
    ) {
     dispatch(
        startJob({
          enrichmentJobId: job.enrichmentJobId!,
          enrichmentDbId: job.id,
          storeName: currentCollection?.collection?.miqShopifyDomain,
          userEmail: currentUser?.email,
          collectionName: currentCollection?.collection?.name,
          tableId: currentCollection?.collection?.tableId,
          ...(exportedFields && { exportFields: exportedFields }),
          enrichmentPendingChanges: enrichmentPendingChanges.filter(
            (change) => change.enrichmentJobId === job.enrichmentJobId
          ),
        })
      );
      try {
        
        // Call startExportToShopify without exportedFields
        const result = await startExportToShopify({
          jobId: job.id
        });
        
        
        if (!result.success) {
          toast({
            title: t('jobsView.export_failed'),
            description: result.error || t('jobsView.unknown_error'),
            variant: "destructive",
          });
        } else {
          // Switch to "all" filter when export starts
          if (jobFilter !== "all") {
            dispatch(setJobFilter("all"));
            
            toast({
              title: t('jobsView.export_started'),
              description: t('jobsView.products_being_exported_to_shopify_switched_to_all_jobs_view_to_track_progress'),
            });
          } else {
            toast({
              title: t('jobsView.export_started'),
              description: t('jobsView.products_being_exported_to_shopify'),
            });
          }
          
          // ExportJobService'i başlat - Bu servis Redux store'u otomatik olarak güncelleyecek
          const exportService = ExportJobService.getInstance(dispatch);
          exportService.ensurePolling();
          
          // İlk durumu kontrol etmek için getEnrichmentTask yerine ExportJobService kullanabiliriz
        }
      } catch (error) {
        console.error("Export error:", error);
        toast({
          title: t('jobsView.export_failed'),
          description: error instanceof Error ? error.message : t('jobsView.unknown_error'),
          variant: "destructive",
        });
      }
    } else {
      alert("Table ID of Shopify domain or user email is missing");
    }
  };

  const exportJob = useSelector(
    (state: RootState) => state.jobs.activeExportJobs[job.enrichmentJobId!]
  );

  const exportError = exportJob?.error;

  
  const currentExportProgress = exportJob?.progress || job.progress || 0;
  
  const progressValue = currentExportProgress;

  const getAvailableFeatures = () => {
    const calculateImprovement = (newScore: number, oldScore: number) => {
      if (!oldScore) return newScore;
      return Math.round(((newScore - oldScore) / oldScore) * 100);
    };

    const productsWithSelectedRadarImagesCount = products.reduce((count, p) => {
      const localStorageKey = JOB_GALLERY_STORAGE_KEY(
        job.enrichmentJobId!,
        p.product_id
      );
      const selectedImages = JSON.parse(
        localStorage.getItem(localStorageKey) || "[]"
      );
      return selectedImages.length > 0 ? count + 1 : count;
    }, 0);

    //also find total selected image count

    const totalSelectedImages = products.reduce((count, p) => {
      const localStorageKey = JOB_GALLERY_STORAGE_KEY(
        job.enrichmentJobId!,
        p.product_id
      );
      const selectedImages = JSON.parse(
        localStorage.getItem(localStorageKey) || "[]"
      );
      return count + selectedImages.length;
    }, 0);

    const features: Record<
      string,
      {
        label: string;
        changed: number;
        improvement: number;
        improvementText?: string;
      }
    > = {
      radar_images: {
        label: "Radar Images",
        changed: productsWithSelectedRadarImagesCount,
        improvement: totalSelectedImages,
        improvementText: `${totalSelectedImages} image selected from ${productsWithSelectedRadarImagesCount} ${
          productsWithSelectedRadarImagesCount > 1 ? "products" : "product"
        }`,
      },
      title: {
        label: "Product Title",
        changed: products.filter((p) => p.old_title !== p.title && p.title)
          .length,
        improvement: calculateImprovement(
          qualityData?.data?.total_title_score || 0,
          qualityData?.data?.old_total_title_score || 0
        ),
      },
      description_html: {
        label: "Product Description",
        changed: products.filter(
          (p) =>
            p.old_description_html !== p.description_html && p.description_html
        ).length,
        improvement: calculateImprovement(
          qualityData?.data?.total_desc_html_score || 0,
          qualityData?.data?.old_total_desc_html_score || 0
        ),
      },
      description_bullets: {
        label: "Description Bullets",
        changed: products.filter(
          (p) =>
            p.old_description_bullets !== p.description_bullets &&
            p.description_bullets
        ).length,
        improvement: calculateImprovement(
          qualityData?.data?.total_description_bullets_score || 0,
          qualityData?.data?.old_total_description_bullets_score || 0
        ),
      },
      category_name: {
        label: "Category",
        changed: products.filter(
          (p) => p.old_category_name !== p.category_name && p.category_name
        ).length,
        improvement: calculateImprovement(
          qualityData?.data?.total_category_placement_score || 0,
          qualityData?.data?.old_total_category_placement_score || 0
        ),
      },
      tags: {
        label: "Tags",
        changed: products.filter((p) => p.old_tags !== p.tags && p.tags).length,
        improvement: calculateImprovement(
          qualityData?.data?.total_tags_score || 0,
          qualityData?.data?.old_total_tags_score || 0
        ),
      },
      seo_title: {
        label: "SEO Title",
        changed: products.filter(
          (p) => p.old_seo_title !== p.seo_title && p.seo_title
        ).length,
        improvement: calculateImprovement(
          qualityData?.data?.total_seo_title_score || 0,
          qualityData?.data?.old_total_seo_title_score || 0
        ),
      },
      seo_description: {
        label: "SEO Description",
        changed: products.filter(
          (p) =>
            p.old_seo_description !== p.seo_description && p.seo_description
        ).length,
        improvement: calculateImprovement(
          qualityData?.data?.total_seo_description_score || 0,
          qualityData?.data?.old_total_seo_description_score || 0
        ),
      },
      alt_text: {
        label: "Image Alt Texts",
        changed: (() => {
          return products.filter((p) => {
            if (!p.old_images_objects || !p.images_objects) return false;
            return p.images_objects.some((img, idx) => {
              const oldImg = p.old_images_objects[idx];
              return oldImg && img.alt_text !== oldImg.alt_text;
            });
          }).length;
        })(),
        improvement: calculateImprovement(
          qualityData?.data?.total_alt_text_score || 0,
          qualityData?.data?.old_total_alt_text_score || 0
        ),
      },
    };

    return Object.fromEntries(
      Object.entries(features).filter(([_, value]) => value.changed > 0)
    );
  };

  // Default to showing preview for demo purposes
  const showPreview = job.isLatest ?? true;
  const previewProduct =
    showPreview && products.length > 0 ? products[0] : null;

  // useEffect(() => {
  //   if (exportError) {
  //     toast({
  //       title: t('jobsView.export_failed'),
  //       description: exportError.failReason,
  //       variant: "destructive",
  //     });
  //   }
  // }, [JSON.stringify(exportError)]);

  const totalPages = Math.ceil(
    (productsData?.data?.content_quality_overview?.filtered_product_count ??
      0) / ITEMS_PER_PAGE
  );

  const productCountToBeExported = job.syncedToShopify
    ? enrichmentPendingChanges?.filter(
        (change) => change.enrichmentJobId === job.enrichmentJobId
      ).length
    : productCount;

  return (
    <>
      <Card
        onMouseEnter={() => {
          if (!job.enrichmentJobId) return;
          dispatch(
            setHoveredJob({
              enrichmentDbId: job.id,
              enrichmentJobId: job.enrichmentJobId,
            })
          );
        }}
        onMouseLeave={() => {
          // dispatch(setHoveredJob(null));
        }}
        ref={cardRef}
        data-testid="job-completed-card"
        className={`w-full transition-all duration-200 hover:shadow-md overflow-hidden
      ${isExpanded ? "invisible" : "visible"}
    `}
      >
        {/* Status Bar */}
        <div>
          <div className="px-6 py-4 flex items-center justify-between bg-white border-b border-gray-100">
            <div className="flex items-center space-x-3">
              {/* Keep existing status badges but adjust styling */}
              <div className="flex items-start md:items-center flex-col gap-2 md:flex-row">
                {showPreview && <Badge variant="secondary">{t('jobsView.latest')}</Badge>}
                <Badge className="bg-green-50 hover:bg-green-50 text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {job.status?.replace("_", " ") ?? t('jobsView.completed')}
                </Badge>
                <span className="text-xs text-gray-500 flex items-center">
                  <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
                  {job.endedAt ? formatDate(job.endedAt) : t('jobsView.time_ago', { time: '2h' })}
                </span>
                {isDebugMode && productsData?.data?.products?.[0]?.job_id && (
                  <span className="ml-4 text-xs text-red-400 font-mono">
                    {t('jobsView.job_id')}: {productsData.data.products[0].job_id}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {!job.syncedToShopify && (
                <ExportControls
                  job={job}
                  isExporting={exportJob?.status === "running"}
                  productCount={productCountToBeExported ?? 0}
                  availableFeatures={getAvailableFeatures()}
                  onExportToShopify={(selectedFeatures) => {
                    if (!job.enrichmentJobId) {
                      toast({
                        title: t('jobsView.export_failed'),
                        description: t('jobsView.enrichment_id_missing'),
                        variant: "destructive",
                      });
                      return;
                    }
                    exportToShopify({
                      exportedFields: selectedFeatures,
                    });
                  }}
                  progress={progressValue}
                  status={exportJob?.currentSubTask}
                  isAnyJobExporting={isAnyJobExporting}
                  isFetching={isFetching}
                  isQualityLoading={isQualityLoading}
                />
              )}

              {job.syncedToShopify && (
                <div className="flex items-center gap-2">
                  <button
                    disabled
                    className="flex w-[120px] md:w-max items-center px-4 py-2 text-sm bg-gray-50 text-gray-500 rounded-lg cursor-not-allowed"
                  >
                    <CheckCircle2 className="flex-shrink-0 w-4 h-4 mr-2" />
                    <span className="hidden md:inline-block">
                      {t('jobsView.products_synced_shopify')}
                    </span>
                    <span className="md:hidden">{t('jobsView.synced')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Metrics Bar */}
          <div
            className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 border-b border-gray-100"
            onClick={handleCardClick}
          >
            <div className="flex items-center gap-8">
              {isFetching || isQualityLoading ? (
                <>
                  <div>
                    <div className="h-9 w-24 bg-gray-200 rounded-md animate-pulse" />
                    <div className="h-4 w-32 bg-gray-200 rounded-md mt-2 animate-pulse" />
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="h-4 w-12 bg-gray-200 rounded-md animate-pulse mb-2" />
                      <div className="h-7 w-16 bg-gray-200 rounded-md animate-pulse" />
                    </div>
                    <div className="h-5 w-5 bg-gray-200 rounded-md animate-pulse" />
                    <div className="text-center">
                      <div className="h-4 w-12 bg-gray-200 rounded-md animate-pulse mb-2" />
                      <div className="h-7 w-16 bg-gray-200 rounded-md animate-pulse" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <span className="text-xl md:text-3xl font-semibold text-gray-900">
                      {productCount}
                    </span>
                    <span className="text-sm text-gray-600 ml-2">
                      {productCount === 1 ? t('jobsView.product_selected_single') : t('jobsView.product_selected_plural', { count: productCount })}
                    </span>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-sm text-gray-500">{t('jobsView.before')}</div>
                      <div className="text-xl font-medium text-gray-700">
                        {enrichmentScore?.overallBefore}%
                      </div>
                    </div>
                    {enrichmentScore?.overallAfter >
                    enrichmentScore?.overallBefore ? (
                      <ArrowUpRight className="h-5 w-5 text-green-500" />
                    ) : enrichmentScore?.overallAfter <
                      enrichmentScore?.overallBefore ? (
                      <ArrowUpRight className="h-5 w-5 text-red-500 rotate-90" />
                    ) : (
                      <div className="h-5 w-5 text-gray-400">-</div>
                    )}
                    <div className="text-center">
                      <div className="text-sm text-gray-500">{t('jobsView.after')}</div>
                      <div
                        className={`text-xl font-medium ${
                          enrichmentScore?.overallAfter >
                          enrichmentScore?.overallBefore
                            ? "text-green-600"
                            : enrichmentScore?.overallAfter <
                                enrichmentScore?.overallBefore
                              ? "text-red-600"
                              : "text-gray-700"
                        }`}
                      >
                        {enrichmentScore?.overallAfter}%
                      </div>
                    </div>
                  </div>
                </>
              )}

              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="px-6 py-4 flex items-center justify-between bg-white border-b border-gray-100">
          {showPreview && previewProduct && !isExpanded && (
            <div
              className="px-6 py-4 cursor-pointer hover:bg-gray-50"
              onClick={handleCardClick}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">
                  {t('jobsView.quick_preview')}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(); // Changed from setIsExpanded
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <span>{t('jobsView.view_all')}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              {(() => {
                // First check for radar images
                if (
                  previewProduct.radar_images &&
                  previewProduct.radar_images.length > 0
                ) {
                  return (
                    <div className="space-y-3 border-l-2 border-green-100 pl-4">
                      <div>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {previewProduct.radar_images
                            .slice(0, 3)
                            .map((image, idx) => (
                              <div key={idx} className="flex-shrink-0">
                                <img
                                  src={image}
                                  alt={`${t('jobsView.radar_image')} ${idx + 1}`}
                                  className="h-24 w-24 object-cover rounded-lg"
                                />
                              </div>
                            ))}
                          {previewProduct.radar_images.length > 3 && (
                            <div className="flex-shrink-0 h-24 w-24 bg-gray-50 rounded-lg flex items-center justify-center">
                              <span className="text-sm text-gray-500">
                                +{previewProduct.radar_images.length - 3} {t('jobsView.more')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                // If no radar images, show other changed fields
                const changedField = getFirstChangedField(previewProduct);
                if (!changedField) return null;

                return (
                  <div className="space-y-3 border-l-2 border-green-100 pl-4">
                    <div>
                      <div className="text-sm text-gray-400">
                        {t('jobsView.original')} {changedField.name}
                      </div>
                      {changedField.name === "Description Bullets" ? (
                        <div className="text-sm text-gray-400">
                          {changedField.old?.split("\n").map((bullet, idx) => (
                            <div key={idx} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{bullet}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div
                          className="text-sm text-gray-400 line-clamp-3"
                          dangerouslySetInnerHTML={{
                            __html: changedField.old || "",
                          }}
                        />
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-900">
                        {t('jobsView.improved')} {changedField.name}
                      </div>
                      {changedField.name === "Description Bullets" ? (
                        <div className="text-sm text-gray-900">
                          {changedField.new?.split("\n").map((bullet, idx) => (
                            <div key={idx} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{bullet}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div
                          className="text-sm text-gray-900 line-clamp-3"
                          dangerouslySetInnerHTML={{
                            __html: changedField.new || "",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </Card>

      {/* Expanded Content - Split Viewccc */}

      {isExpanded && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/10  z-[100] transition-opacity ease-out"
            onClick={handleClose}
          />

          {/* Main overlay */}
          <div
            ref={overlayRef}
            className="fixed inset-0 bg-white z-[101] overflow-auto"
            style={{
              transformOrigin: "0 0",
              transition: "all 100ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div
              className={`
        w-full mx-auto p-6 transition-all  ease-out
        ${
          isContentVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-2"
        }
      `}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-6 max-w-[1400px] mx-auto">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleClose}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span>{t('jobsView.back_to_jobs')}</span>
                  </button>
                  <h2 className="text-2xl font-bold ml-2">
                    {job.status === "completed"
                      ? t('jobsView.completed_job_details')
                      : t('jobsView.job_details')}
                  </h2>
                </div>

                <div className="flex items-center gap-4">
                  {/* Add Export Controls here */}
                  {!job.syncedToShopify && (
                    <ExportControls
                      job={job}
                      isExporting={exportJob?.status === "running"}
                      productCount={productCountToBeExported ?? 0}
                      availableFeatures={getAvailableFeatures()}
                      onExportToShopify={(selectedFeatures) => {
                        if (!job.enrichmentJobId) {
                          toast({
                            title: t('jobsView.export_failed'),
                            description: t('jobsView.enrichment_id_missing'),
                            variant: "destructive",
                          });
                          return;
                        }
                        exportToShopify({
                          exportedFields: selectedFeatures,
                        });
                      }}
                      progress={progressValue}
                      status={exportJob?.currentSubTask}
                      isAnyJobExporting={isAnyJobExporting}
                      isFetching={isFetching}
                      isQualityLoading={isQualityLoading}
                    />
                  )}

                  <button
                    onClick={handleClose}
                    className="p-3 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div
                className={`
          flex flex-col md:h-[calc(100vh-120px)] md:flex-row gap-6 max-w-[1400px] mx-auto
          transition-all duration-300 ease-out
          ${
            isContentVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          }
        `}
              >
                {/* The rest of your content remains the same */}
                <CardContent
                  className="pt-4 w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isFetching ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      <span>{t('jobsView.loading_products')}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col md:h-[calc(100vh-220px)] md:flex-row gap-6">
                      {" "}
                      {/* Product List - Left Side */}
                      <div className="w-full md:w-1/4 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
                        {" "}
                        {/* Added flex flex-col */}
                        <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                          <div>
                            <h2 className="font-medium text-gray-900">
                              {t('jobsView.enhanced_products')}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                              {
                                productsData?.data?.content_quality_overview
                                  .filtered_product_count
                              }{" "}
                              {productsData?.data?.content_quality_overview
                                .filtered_product_count === 1
                                ? t('jobsView.improvement_single')
                                : t('jobsView.improvements_plural')}
                            </p>
                          </div>
                        </div>
                        <div
                          className="overflow-auto"
                          style={{ maxHeight: "calc(100vh - 220px)" }}
                        >
                          {products.map((product, index) => (
                            <div
                              key={`${product.product_id}_${index}`}
                              onClick={() => {
                                const key = `${product.product_id}_${index}`;
                                setExpandedProductIds(new Set([key]));
                              }}
                              className={`p-4 border-b border-gray-100 cursor-pointer transition-all ${
                                expandedProductIds.has(
                                  `${product.product_id}_${index}`
                                )
                                  ? "bg-blue-50 border-l-4 border-l-blue-500"
                                  : "hover:bg-gray-50"
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                {hasAnyChanges(product.product_id) && (
                                  <div className="bg-orange-400 w-[8px] h-[8px] rounded-full block relative" />
                                )}

                                <div className="h-12 w-12 bg-gray-100 rounded-lg flex-shrink-0">
                                  {(() => {
                                    const featuredImage =
                                      product.featured_image ||
                                      product.images_objects?.[0]?.image_url;
                                    const altText = product.featured_image
                                      ? t('jobsView.product_image')
                                      : product.images_objects?.[0]?.alt_text;
                                    return (
                                      <>
                                        {featuredImage ? (
                                          <img
                                            src={featuredImage}
                                            alt={altText}
                                            className="rounded-lg w-full h-full object-cover"
                                            onError={(e) => {
                                              e.currentTarget.style.display =
                                                "none";
                                              e.currentTarget.parentElement
                                                ?.querySelector(
                                                  ".fallback-icon"
                                                )
                                                ?.classList.remove("hidden");
                                            }}
                                          />
                                        ) : null}
                                        <div
                                          className={`w-full h-full rounded-lg flex items-center justify-center bg-gray-100 fallback-icon ${
                                            featuredImage ? "hidden" : ""
                                          }`}
                                        >
                                          <ShoppingCart className="w-6 h-6 text-gray-400" />
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                                      {product.title || product.title}
                                    </h3>
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    {product.old_quality_score &&
                                      product.new_quality_score && (
                                        <div className="flex items-center space-x-1 text-sm">
                                          <ArrowUpRight className="h-3 w-3 text-green-500" />
                                          <span className="text-green-600">
                                            +
                                            {product.new_quality_score -
                                              product.old_quality_score}
                                            %
                                          </span>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="my-4">
                          {/* Pagination */}
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    setCurrentPage((prev) =>
                                      Math.max(1, prev - 1)
                                    )
                                  }
                                  disabled={currentPage === 1}
                                  className="h-8 w-8"
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </Button>
                              </PaginationItem>

                              {Array.from(
                                { length: totalPages },
                                (_, i) => i + 1
                              )
                                .filter((pageNum) => {
                                  if (pageNum === 1 || pageNum === totalPages)
                                    return true;
                                  return Math.abs(currentPage - pageNum) <= 2;
                                })
                                .map((pageNum, i, array) => {
                                  if (i > 0 && pageNum - array[i - 1] > 1) {
                                    return (
                                      <React.Fragment
                                        key={`${pageNum}-fragment`}
                                      >
                                        <PaginationItem>
                                          <span className="px-2">...</span>
                                        </PaginationItem>
                                        <PaginationItem>
                                          <PaginationLink
                                            onClick={() =>
                                              setCurrentPage(pageNum)
                                            }
                                            isActive={currentPage === pageNum}
                                            className="h-8 w-8 p-0 flex items-center justify-center"
                                          >
                                            {pageNum}
                                          </PaginationLink>
                                        </PaginationItem>
                                      </React.Fragment>
                                    );
                                  }

                                  return (
                                    <PaginationItem key={pageNum}>
                                      <PaginationLink
                                        onClick={() => setCurrentPage(pageNum)}
                                        isActive={currentPage === pageNum}
                                        className="h-8 w-8 p-0 flex items-center justify-center"
                                      >
                                        {pageNum}
                                      </PaginationLink>
                                    </PaginationItem>
                                  );
                                })}

                              <PaginationItem>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    setCurrentPage((prev) =>
                                      Math.min(totalPages, prev + 1)
                                    )
                                  }
                                  disabled={currentPage === totalPages}
                                  className="h-8 w-8"
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      </div>
                      {!isMobile && (
                        <div className="flex-1 min-w-0 overflow-hidden">
                          {" "}
                          {Array.from(expandedProductIds).map((key) => {
                            const [productId, index] = key.split("_");
                            const product = products.find(
                              (p) => p.product_id === productId
                            );
                            if (!product) return null;

                            return (
                              <ProductDetail
                                key={key}
                                product={product}
                                jobId={job.enrichmentJobId!}
                              />
                            );
                          })}
                        </div>
                      )}
                      <Drawer
                        open={isDrawerOpen}
                        onOpenChange={setDrawerOpen}
                        dismissible={true}
                      >
                        <DrawerContent>
                          <div className="p-4 max-h-[calc(100vh-120px)] overflow-auto">
                            {Array.from(expandedProductIds).map((key) => {
                              const [productId, index] = key.split("_");
                              const product = products.find(
                                (p) => p.product_id === productId
                              );
                              if (!product) return null;

                              return (
                                <ProductDetail
                                  key={key}
                                  product={product}
                                  jobId={job.enrichmentJobId!}
                                />
                              );
                            })}
                          </div>
                          <DrawerFooter></DrawerFooter>
                        </DrawerContent>
                      </Drawer>
                    </div>
                  )}
                </CardContent>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default JobCompletedCard;
